import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Box, Typography, ClickAwayListener, Popper, InputBase, Chip } from '@mui/material';
import { KeyboardArrowDown, Search, Check, Close } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

export interface FilterDropdownOption {
    value: string;
    label: string;
    count?: number;
}

interface FilterDropdownProps {
    label: string;
    value: string;
    options: FilterDropdownOption[];
    onChange: (value: string) => void;
    allLabel?: string;
    allValue?: string;
    minWidth?: number;
    searchThreshold?: number;
    disabled?: boolean;
    /** Label sits inside the box (default) instead of above it, saving a row of height. */
    compact?: boolean;
}

// Searchable, animated replacement for MUI's plain Select — used for every filter
// dropdown in Bundle Categories / Simple-Bundle view / Component view. Some of these
// lists (Category, Sub-Category, Brand) run past 20 options with no way to type-to-find,
// so search kicks in automatically once a list is long enough to need it.
const FilterDropdown: React.FC<FilterDropdownProps> = ({
    label,
    value,
    options,
    onChange,
    allLabel,
    allValue = 'all',
    minWidth = 150,
    searchThreshold = 8,
    disabled = false,
    compact = true,
}) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [highlighted, setHighlighted] = useState(0);
    const anchorRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    const allOptions: FilterDropdownOption[] = useMemo(
        () => (allLabel ? [{ value: allValue, label: allLabel }, ...options] : options),
        [allLabel, allValue, options]
    );

    const filtered = useMemo(() => {
        if (!query.trim()) return allOptions;
        const q = query.trim().toLowerCase();
        return allOptions.filter(o => o.label.toLowerCase().includes(q));
    }, [allOptions, query]);

    const showSearch = allOptions.length > searchThreshold;
    const selected = allOptions.find(o => o.value === value);
    const isActive = allLabel ? value !== allValue : Boolean(value);

    useEffect(() => {
        if (open) {
            setQuery('');
            setHighlighted(0);
            if (showSearch) {
                setTimeout(() => searchRef.current?.focus(), 60);
            }
        }
    }, [open, showSearch]);

    const handleSelect = (val: string) => {
        onChange(val);
        setOpen(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlighted(h => Math.min(h + 1, filtered.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlighted(h => Math.max(h - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const opt = filtered[highlighted];
            if (opt) handleSelect(opt.value);
        } else if (e.key === 'Escape') {
            setOpen(false);
        }
    };

    return (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Box sx={{ position: 'relative', minWidth }}>
                {!compact && (
                    <Typography
                        sx={{
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            color: 'text.secondary',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            mb: 0.4,
                            ml: 0.25,
                        }}
                    >
                        {label}
                    </Typography>
                )}
                <Box
                    ref={anchorRef}
                    onClick={() => !disabled && setOpen(o => !o)}
                    onKeyDown={(e) => {
                        if (!open && (e.key === 'Enter' || e.key === ' ')) {
                            e.preventDefault();
                            setOpen(true);
                        } else if (open) {
                            handleKeyDown(e);
                        }
                    }}
                    tabIndex={disabled ? -1 : 0}
                    role="combobox"
                    aria-expanded={open}
                    aria-haspopup="listbox"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        height: compact ? 36 : 40,
                        px: 1.25,
                        borderRadius: '10px',
                        bgcolor: '#ffffff',
                        border: '1px solid',
                        borderColor: open ? 'primary.main' : isActive ? 'primary.main' : '#ECECE4',
                        boxShadow: open ? '0 0 0 3px rgba(220, 88, 41, 0.12)' : 'none',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        opacity: disabled ? 0.5 : 1,
                        transition: 'border-color 150ms ease, box-shadow 150ms ease',
                        '&:hover': disabled ? {} : { borderColor: '#DC5829' },
                        outline: 'none',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, minWidth: 0 }}>
                        {compact && (
                            <Typography component="span" sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#8A8078', textTransform: 'uppercase', letterSpacing: '0.06em', flexShrink: 0 }}>
                                {label}
                            </Typography>
                        )}
                        <Typography
                            noWrap
                            sx={{
                                fontSize: compact ? '0.82rem' : '0.875rem',
                                fontWeight: isActive ? 700 : 500,
                                color: isActive ? 'text.primary' : 'text.secondary',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {selected?.label ?? allLabel ?? 'Select…'}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                        {isActive && allLabel && (
                            <Box
                                component="span"
                                onClick={(e) => { e.stopPropagation(); handleSelect(allValue); }}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 18,
                                    height: 18,
                                    borderRadius: '50%',
                                    color: 'text.secondary',
                                    '&:hover': { bgcolor: '#F3F2EC', color: '#DC5829' },
                                }}
                            >
                                <Close sx={{ fontSize: 14 }} />
                            </Box>
                        )}
                        <motion.div
                            animate={{ rotate: open ? 180 : 0 }}
                            transition={{ duration: 0.18, ease: 'easeOut' }}
                            style={{ display: 'flex' }}
                        >
                            <KeyboardArrowDown sx={{ fontSize: 20, color: 'text.secondary' }} />
                        </motion.div>
                    </Box>
                </Box>

                <Popper
                    open={open}
                    anchorEl={anchorRef.current}
                    placement="bottom-start"
                    sx={{ zIndex: 1400, width: Math.max(anchorRef.current?.offsetWidth ?? 0, 220) }}
                    modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
                >
                    <AnimatePresence>
                        {open && (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.96, y: -4 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.96, y: -4 }}
                                transition={{ duration: 0.14, ease: [0.16, 1, 0.3, 1] }}
                                style={{ transformOrigin: 'top center' }}
                            >
                                <Box
                                    sx={{
                                        bgcolor: '#ffffff',
                                        borderRadius: '12px',
                                        border: '1px solid #ECECE4',
                                        boxShadow: '0 12px 32px rgba(26, 20, 15, 0.16), 0 2px 8px rgba(26, 20, 15, 0.08)',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {showSearch && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.75,
                                                px: 1.25,
                                                py: 1,
                                                borderBottom: '1px solid #F3F2EC',
                                            }}
                                        >
                                            <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
                                            <InputBase
                                                inputRef={searchRef}
                                                value={query}
                                                onChange={(e) => { setQuery(e.target.value); setHighlighted(0); }}
                                                onKeyDown={handleKeyDown}
                                                placeholder={`Search ${label.toLowerCase()}…`}
                                                fullWidth
                                                sx={{ fontSize: '0.875rem' }}
                                            />
                                        </Box>
                                    )}
                                    <Box
                                        role="listbox"
                                        sx={{
                                            maxHeight: 300,
                                            overflowY: 'auto',
                                            py: 0.5,
                                            '&::-webkit-scrollbar': { width: 6 },
                                            '&::-webkit-scrollbar-thumb': { bgcolor: '#DAD6CC', borderRadius: 3 },
                                        }}
                                    >
                                        {options.length === 0 && (
                                            <Typography sx={{ px: 2, py: 1.5, fontSize: '0.8rem', color: 'text.secondary', textAlign: 'center' }}>
                                                No options available
                                            </Typography>
                                        )}
                                        {options.length > 0 && filtered.length === 0 && (
                                            <Typography sx={{ px: 2, py: 1.5, fontSize: '0.8rem', color: 'text.secondary', textAlign: 'center' }}>
                                                No matches
                                            </Typography>
                                        )}
                                        {filtered.map((opt, idx) => {
                                            const isSelected = opt.value === value;
                                            const isHighlighted = idx === highlighted;
                                            return (
                                                <Box
                                                    key={opt.value}
                                                    role="option"
                                                    aria-selected={isSelected}
                                                    onMouseEnter={() => setHighlighted(idx)}
                                                    onClick={() => handleSelect(opt.value)}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        gap: 1,
                                                        px: 1.5,
                                                        py: 0.9,
                                                        mx: 0.5,
                                                        borderRadius: '8px',
                                                        cursor: 'pointer',
                                                        bgcolor: isHighlighted ? 'hsl(20, 85%, 96%)' : 'transparent',
                                                        transition: 'background-color 100ms ease',
                                                    }}
                                                >
                                                    <Typography
                                                        noWrap
                                                        sx={{
                                                            fontSize: '0.875rem',
                                                            fontWeight: isSelected ? 600 : 400,
                                                            color: isSelected ? '#DC5829' : '#1A1410',
                                                        }}
                                                    >
                                                        {opt.label}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                                                        {typeof opt.count === 'number' && (
                                                            <Chip
                                                                label={opt.count}
                                                                size="small"
                                                                sx={{
                                                                    height: 18,
                                                                    fontSize: '0.7rem',
                                                                    bgcolor: '#F3F2EC',
                                                                    '& .MuiChip-label': { px: 0.75 },
                                                                }}
                                                            />
                                                        )}
                                                        {isSelected && <Check sx={{ fontSize: 16, color: '#DC5829' }} />}
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </Popper>
            </Box>
        </ClickAwayListener>
    );
};

export default FilterDropdown;
